import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";
import {
  DeleteTeeSheetNotificationTemplate,
  GetTeeSheetNotificationTemplate,
  PutTeeSheetNotificationTemplate,
  TTeesheetNotificationType,
} from "api/rpc/2024-04/facilityAdmin/teesheet/notification";

import { showError } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";

import Page from "components/page/Page";
import Card from "components/card/Card";
import Popup from "components/popup/Popup";
import Input from "components/form/input/Input";
import FormLayout from "components/form/FormLayout";
import { Select } from "components/select";
import useUnsavedChangesContainer from "hooks/useUnsavedChangesContainer/useUnsavedChangesContainer";
import { isEmpty } from "helpers/Helpers";
import TextEditor from "components/textEditor/textEditor";

interface INotificationTemplateSaveState {
  id: string;
  subject: string;
  content: string;
  userType: TTeesheetNotificationType;
}

interface INotificationUserTypeOption {
  label: string;
  value: TTeesheetNotificationType;
}

export default function TeeSheetNotificationTemplate() {
  const { templateId } = useParams<{ templateId: string }>();
  const { t } = useTranslation();
  const history = useHistory();

  const dispatch = useAppDispatch();

  const notificationUserTypeOptions: INotificationUserTypeOption[] = useMemo(() => {
    return [
      {
        label: t("Internal"),
        value: "admin",
      },
      {
        label: t("External"),
        value: "customer",
      },
    ];
  }, []);

  const [deletePopupVisible, setDeletePopupVisible] = useState<boolean>(false);
  const [loadedTemplate, setLoadedTemplate] = useState<boolean>(false);

  const [saveStateContainer, saveStateContainerActions] = useUnsavedChangesContainer<INotificationTemplateSaveState>();

  useEffect(() => {
    if (!loadedTemplate) {
      void loadTemplate();
    }
  }, [templateId]);

  async function loadTemplate() {
    const response = await GetTeeSheetNotificationTemplate({ id: templateId }, true);

    if (response.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading notification template.")));
      return;
    }

    if (Array.isArray(response.data)) {
      dispatch(showError(t("Error loading notification template.")));
      return;
    }

    const template = response.data;

    const saveState: INotificationTemplateSaveState = {
      id: templateId,
      subject: template.subject,
      content: template.content,
      userType: template.user_type,
    };

    saveStateContainerActions.set(saveState);
    setLoadedTemplate(true);
  }

  async function saveTemplate() {
    if (isEmpty(saveStateContainer.updatedState?.subject)) {
      return;
    }

    const response = await PutTeeSheetNotificationTemplate(
      {
        id: Number(saveStateContainer.updatedState?.id),
        subject: saveStateContainer.updatedState?.subject,
        content: saveStateContainer.updatedState?.content,
        user_type: saveStateContainer.updatedState?.userType,
      },
      true,
    );

    if (response.status !== StatusCode.OK) {
      dispatch(showError(response.data as string));
      return;
    }

    saveStateContainerActions.save();
    history.push("/admin/settings/tee-sheet/notification-templates");
  }

  function cancelUnsavedChanges() {
    saveStateContainerActions.rollback();
  }

  async function handleTemplateDelete() {
    const response = await DeleteTeeSheetNotificationTemplate({ id: templateId }, true);

    if (response.status !== StatusCode.OK) {
      dispatch(showError(response.data as string));
      return;
    }

    history.push("/admin/settings/tee-sheet/notification-templates");
  }

  const primaryAction = {
    content: t("Delete"),
    action: () => setDeletePopupVisible(true),
  };

  return (
    <Page
      title={`${t("Notification Template")} #${templateId}`}
      narrow
      breadcrumbs={[
        {
          prefix: true,
          label: t("Back to notification templates"),
          url: "/admin/settings/tee-sheet/notification-templates",
        },
      ]}
      primaryAction={primaryAction}
      notificationBarProps={{
        isVisible: saveStateContainer.changesExist,
        onAction: saveTemplate,
        onCancel: cancelUnsavedChanges,
      }}
    >
      {loadedTemplate && (
        <Card>
          <Card.Section>
            <FormLayout>
              <FormLayout.Group>
                <Select
                  label={t("User Type")}
                  onChange={(userType: TTeesheetNotificationType) =>
                    saveStateContainerActions.update(previousState => ({ ...previousState, userType }))
                  }
                  defaultValue={saveStateContainer.updatedState?.userType}
                >
                  {notificationUserTypeOptions.map((option, optionIndex: number) => {
                    return (
                      <Select.Option key={optionIndex} value={option.value} name={option.label}>
                        {option.label}
                      </Select.Option>
                    );
                  })}
                </Select>
              </FormLayout.Group>
              <FormLayout.Group>
                <Input
                  value={saveStateContainer.updatedState?.subject}
                  label={t("Header")}
                  onChange={event =>
                    saveStateContainerActions.update(previousState => ({ ...previousState, subject: event.target.value }))
                  }
                  placeholder={t("Header")}
                  error={isEmpty(saveStateContainer.updatedState?.subject)}
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <TextEditor
                  markdownText={saveStateContainer.updatedState?.content || ""}
                  markdownTextOnChange={newContent =>
                    saveStateContainerActions.update(previousState => ({ ...previousState, content: newContent }))
                  }
                  label={t("Contents")}
                />
              </FormLayout.Group>
            </FormLayout>
          </Card.Section>
        </Card>
      )}

      <Popup
        onCancel={() => setDeletePopupVisible(false)}
        onOk={handleTemplateDelete}
        cancelText={t("Close")}
        okText={t("Delete")}
        open={deletePopupVisible}
        type="warning"
        title={t("Confirm Template Delete")}
        description={t("Are you sure you want to delete this template?")}
      />
    </Page>
  );
}

